/**
 * Client-side helpers for the CRM plugin. The row/response shapes derive from
 * the server's route defs (Phase 7) via `./api` and are re-exported here so
 * pages keep one import point; only UI-only helpers are declared by hand.
 */
import { timeAgo } from "@d12labs/utils";
import type { Activity } from "./api";

export type { Activity, Attachment, Contact, ContactWithDeals, Deal } from "./api";

/**
 * Relative-time label — the shared `timeAgo` primitive, widened to accept the
 * derived rows' Date-typed timestamps (ISO strings on the wire).
 */
export const formatRelativeTime = (at: string | Date): string =>
	timeAgo(at instanceof Date ? at.toISOString() : at);

export const describeActivity = (a: Activity): string => {
	// The jsonb payload is untyped on the wire — the server's ActivityPayload
	// union is the writers' contract; the UI reads it loosely.
	const p = a.payload as Record<string, unknown>;
	switch (a.type) {
		case "email_sent":
			return `Sent email "${p.subject}"`;
		case "deal_created":
			return `Created deal "${p.title}"`;
		case "deal_status_changed":
			return `Deal "${p.dealTitle}" moved from ${p.from} to ${p.to}`;
		case "attachment_uploaded":
			return `Uploaded ${p.filename}`;
		default:
			return a.type;
	}
};

/** File-size label — re-exported from the shared `formatBytes` primitive. */
export { formatBytes } from "@d12labs/utils";

/** Mirrors the server's `crm_deals_status_check` — the derived row carries plain string. */
export const DEAL_STATUSES = ["open", "won", "lost"] as const;
export type DealStatus = (typeof DEAL_STATUSES)[number];

export const formatMoney = (cents: number) =>
	new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
